import PropTypes from 'prop-types';
import React from 'react';
import { Image } from 'react-bootstrap';
import Carousel from 'nuka-carousel';
import Arrow from './Arrow';
import imageResizer from '../helpers/imageResizer';
import './Slider.scss';

const THREE_UP_RATIO = 1.2;

class Slider extends React.Component {
	constructor(props) {
		super(props);

		const { innerWidth, currentIndex } = props;

		this.state = {
			loadedIndex: 3,
			viewportWidth: innerWidth,
			currentIndex: currentIndex || 0
		};
	}
	componentWillReceiveProps(nextProps) {
		// Set a unique prop for image width so that images don't
		// repaint when the state updates the innerWidth property.
		const newState = { ...this.state };

		const { innerWidth } = nextProps;

		if (!newState.viewportWidth) {
			newState.viewportWidth = innerWidth;
		}

		if (newState.currentIndex !== nextProps.currentIndex) {
			newState.currentIndex = nextProps.currentIndex;

			// if index changes, assume gallery was interacted with, load all images
			newState.loadedIndex = nextProps.images.length;
		}

		this.setState(newState);
	}

	shouldComponentUpdate(nextProps, nextState) {
		// don't care about the method changes (these should maybe be updated to dispatched actions)
		const ignoreProps = ['onCurrentIndexChange', 'onPhotoClick', 'trackGa'];

		const nextPropsKeys = Object.keys(nextProps);
		let hasNewProps = false;
		nextPropsKeys.forEach(propKey => {
			if (
				ignoreProps.indexOf(propKey) === -1 &&
				nextProps[propKey] !== this.props[propKey]
			) {
				hasNewProps = true;
			}
		});

		const nextStateKeys = Object.keys(nextState);
		let hasNewState = false;
		nextStateKeys.forEach(stateKey => {
			if (
				stateKey !== 'currentIndex' &&
				nextState[stateKey] !== this.state[stateKey]
			) {
				hasNewState = true;
			}
		});

		return hasNewProps || hasNewState;
	}

	render() {
		const { currentIndex, viewportWidth } = this.state;
		const {
			innerWidth,
			threeUpDisplay,
			imageMode,
			responsiveImage,
			onPhotoClick,
			trackGa,
			images,
			widgetInfo: { deviceType },
			onCurrentIndexChange
		} = this.props;
		const numSlides = images.length;
		const slidesToShow = threeUpDisplay ? 3 : 1;

		const sliderProps = {
			dots: false,
			wrapAround: true,
			speed: 500,
			slideIndex: currentIndex,
			slidesToShow,
			slidesToScroll: 1,
			slideWidth: threeUpDisplay ? THREE_UP_RATIO : 1,
			ref: 'slider',
			beforeSlide: (current, newIndex) => {
				if (newIndex !== this.props.currentIndex) {
					onCurrentIndexChange(newIndex);
				}
			},
			// Only show arrows if needed
			decorators:
				(numSlides > 2 || (!threeUpDisplay && numSlides > 1)) &&
				/desktop/gi.test(deviceType)
					? [
							{
								component: arrowProps => (
									<Arrow
										direction="right"
										onClickEvent={() => {
											arrowProps.nextSlide();
											trackGa({
												element:
													'Photo Carousel Next button',
												result: 'Move to next photo'
											});
										}}
									/>
								),
								position: 'CenterRight',
								style: {
									right: 16
								}
							},
							{
								component: arrowProps => (
									<Arrow
										direction="left"
										classes={null}
										type="prev"
										onClickEvent={() => {
											arrowProps.previousSlide();
											trackGa({
												element:
													'Photo Carousel Previous button',
												result: 'Move to previous photo'
											});
										}}
									/>
								),
								position: 'CenterLeft',
								style: {
									left: 16
								}
							}
					  ]
					: []
		};

		const photoClickHandler = index => {
			const {
				refs: {
					slider: { touchObject }
				}
			} = this;
			if (Object.keys(touchObject).length === 0) {
				onPhotoClick(index);
			}
		};

		const classes = [];
		if (numSlides === 1 && !imageMode) {
			classes.push('single-slide');
		}

		const blankImage = 'https://static.dealer.com/images/blank.gif';
		let firstImageSrc = blankImage;

		const slides = images.map(image => {
			const { id, src } = image;
			const loaded = id <= this.state.loadedIndex;

			let imageSrc = blankImage;
			if (loaded) {
				imageSrc = imageResizer(
					threeUpDisplay,
					viewportWidth,
					src,
					THREE_UP_RATIO
				);
			} else {
				classes.push('d-none');
			}

			// Grab the first image for the background
			if (firstImageSrc === blankImage) {
				firstImageSrc = imageSrc;
			}

			const style = {
				background: `url(${imageSrc}) no-repeat center/cover`
			};

			let imageComponent;
			if (imageMode) {
				imageComponent = (
					<Image
						key={id}
						src={imageSrc}
						className={classes.join(' ')}
						onLoad={() => {
							// Fire a resize event to force a redraw of the carousel so it can recalculate the height
							if (id === 0) {
								window.dispatchEvent(new Event('resize'));
							}
						}}
						onClick={() => {
							photoClickHandler(id);
						}}
						role="button"
						tabIndex={id}
						onKeyDown={e => {
							// If the user tabs to a slide and hits enter, open the modal
							if (e.key === 'Enter' || e.keyCode === 13) {
								onPhotoClick(id);
							}
						}}
					/>
				);
				if (!responsiveImage && !threeUpDisplay) {
					// Wrap component to use the image as its own background
					imageComponent = (
						<div className="blurred-container" key={id}>
							<div style={style} className="blurred-background" />
							{imageComponent}
						</div>
					);
				}
			} else {
				imageComponent = (
					<div
						key={id}
						style={style}
						className={classes.join(' ')}
						onClick={() => {
							photoClickHandler(id);
						}}
						role="button"
						tabIndex={id}
						onKeyDown={e => {
							// If the user tabs to a slide and hits enter, open the modal
							if (e.key === 'Enter' || e.keyCode === 13) {
								onPhotoClick(id);
							}
						}}
					/>
				);
			}
			return imageComponent;
		});

		const carouselClasses = [
			'photo-carousel',
			!imageMode || !innerWidth ? 'background-mode' : 'image-mode',
			threeUpDisplay ? 'add-border' : ''
		];

		let carouselComponent;
		if (!responsiveImage && threeUpDisplay) {
			// Use first image for background
			const carouselStyle = {
				background: `url(${firstImageSrc}) no-repeat right/contain`
			};
			carouselComponent = (
				// Add blurred background
				<div className="blurred-container">
					<div style={carouselStyle} className="blurred-background" />
					<div className={carouselClasses.join(' ')}>
						<Carousel {...sliderProps}>{slides}</Carousel>
					</div>
				</div>
			);
		} else {
			carouselComponent = (
				<div className={carouselClasses.join(' ')}>
					<Carousel {...sliderProps}>{slides}</Carousel>
				</div>
			);
		}
		return carouselComponent;
	}
}

Slider.defaultProps = {
	currentIndex: 0,
	nextSlide: () => {},
	previousSlide: () => {},
	onPhotoClick: () => {}
};

Slider.propTypes = {
	images: PropTypes.arrayOf(
		PropTypes.shape({
			id: PropTypes.number,
			src: PropTypes.string,
			thumbnail: PropTypes.string
		})
	).isRequired,

	// renderSettings
	innerHeight: PropTypes.number.isRequired,
	innerWidth: PropTypes.number.isRequired,
	imageMode: PropTypes.bool.isRequired,
	responsiveImage: PropTypes.bool.isRequired,
	threeUpDisplay: PropTypes.bool.isRequired,
	currentIndex: PropTypes.number,
	onCurrentIndexChange: PropTypes.func.isRequired,

	nextSlide: PropTypes.func,
	previousSlide: PropTypes.func,
	trackGa: PropTypes.func.isRequired,
	onPhotoClick: PropTypes.func,
	widgetInfo: PropTypes.shape({
		deviceType: PropTypes.string
	}).isRequired
};

export default Slider;



// WEBPACK FOOTER //
// ./src/components/Slider.jsx